import { collapseOpenItems } from './collapse-items.js';

function runRowExpandAction(rowId, mode, toggleOpenItemById) {
    if (!rowId) return;
    if (mode === 'actions') {
        toggleOpenItemById({
            itemId: rowId,
            openClass: 'actions-expanded',
            closeClass: 'details-expanded',
        });
        return;
    }
    if (mode === 'details') {
        const result = toggleOpenItemById({
            itemId: rowId,
            openClass: 'details-expanded',
            closeClass: 'actions-expanded',
        });
        const item = result.item;
        if (!item) return;
        item.dispatchEvent(new CustomEvent('data-grid-row-expand-change', {
            bubbles: true,
            detail: { rowId: rowId, expanded: !!result.expanding },
        }));
        if (result.expanding) {
            item.dispatchEvent(new CustomEvent('data-grid-row-details-expand', { bubbles: true, detail: { rowId } }));
        }
    }
}

function shouldIgnorePillExpandTarget(target) {
    return !!target.closest('input[type=checkbox],.data-grid-mobile-pill-actions-btn,.action-btn,.stack-btn');
}

const rootBindings = new WeakMap();

export function createToggleRowHelpers(collapseFn) {
    function toggleOpenItemById(options) {
        const o = options || {};
        const root = o.root || document;
        const itemSelector = o.itemSelector || '.data-grid-item';
        const idAttr = o.idAttr || 'data-row-id';
        const itemId = o.itemId == null ? '' : String(o.itemId);
        const openClass = o.openClass || 'details-expanded';
        const closeClass = o.closeClass || 'actions-expanded';
        const item = root.querySelector(itemSelector + '[' + idAttr + '="' + itemId + '"]');
        if (!item) return { item: null, expanding: false };
        const expanding = !item.classList.contains(openClass);
        if (expanding) {
            collapseFn({
                root,
                itemSelector,
                idAttr,
                exceptId: itemId,
                stateClasses: [openClass, closeClass],
            });
        }
        item.classList.toggle(openClass);
        item.classList.remove(closeClass);
        return { item, expanding };
    }

    return toggleOpenItemById;
}

const defaultToggleOpenItemById = createToggleRowHelpers(collapseOpenItems);

function unbindMobileExpandDelegates(root) {
    const binding = rootBindings.get(root);
    if (!binding) return;
    root.removeEventListener('click', binding.onClick);
    root.removeEventListener('keydown', binding.onKeydown);
    binding.lifetime.abort();
    rootBindings.delete(root);
}

export function bindMobileExpandDelegates(root, toggleOpenItemById, options) {
    if (!(root instanceof HTMLElement)) {
        throw new Error('bindMobileExpandDelegates: root HTMLElement is required');
    }
    if (typeof toggleOpenItemById !== 'function') {
        throw new Error('bindMobileExpandDelegates: toggleOpenItemById is required');
    }

    unbindMobileExpandDelegates(root);

    const externalSignal = options && options.signal;
    const lifetime = new AbortController();
    if (externalSignal) {
        if (externalSignal.aborted) {
            return;
        }
        externalSignal.addEventListener('abort', () => {
            unbindMobileExpandDelegates(root);
        }, { once: true });
    }

    const toggleInRoot = (opts) => toggleOpenItemById({ ...(opts || {}), root });

    function onClick(event) {
        const actionButton = event.target.closest('.data-grid-mobile-pill-actions-btn[data-expand-row-id]');
        if (actionButton && root.contains(actionButton)) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            runRowExpandAction(
                actionButton.getAttribute('data-expand-row-id'),
                actionButton.getAttribute('data-expand-mode') || 'actions',
                toggleInRoot
            );
            return;
        }

        const pill = event.target.closest('.data-grid-mobile-pill');
        if (!pill || !root.contains(pill)) return;
        if (event.target.closest('.data-grid-mobile-actions-panel, .data-grid-mobile-details')) return;
        const pillHeader = pill.querySelector('.data-grid-mobile-pill-header');
        if (!pillHeader) return;
        if (shouldIgnorePillExpandTarget(event.target)) return;
        const expandable = pillHeader.querySelector('.data-grid-pill-expandable[data-expand-row-id]');
        if (!expandable) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        runRowExpandAction(
            expandable.getAttribute('data-expand-row-id'),
            expandable.getAttribute('data-expand-mode') || 'details',
            toggleInRoot
        );
    }

    function onKeydown(event) {
        const pill = event.target.closest('.data-grid-pill-expandable[data-expand-row-id]');
        if (!pill || !root.contains(pill)) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        runRowExpandAction(
            pill.getAttribute('data-expand-row-id'),
            pill.getAttribute('data-expand-mode') || 'details',
            toggleInRoot
        );
    }

    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeydown);
    rootBindings.set(root, { onClick, onKeydown, lifetime });
}

export function toggleDataGridRowActions(rowId, root) {
    defaultToggleOpenItemById({
        root: root || document,
        itemId: rowId,
        openClass: 'actions-expanded',
        closeClass: 'details-expanded',
    });
}

export function toggleDataGridRowDetails(rowId, root) {
    const result = defaultToggleOpenItemById({
        root: root || document,
        itemId: rowId,
        openClass: 'details-expanded',
        closeClass: 'actions-expanded',
    });
    const item = result.item;
    if (!item) return;
    item.dispatchEvent(new CustomEvent('data-grid-row-expand-change', {
        bubbles: true,
        detail: { rowId: rowId, expanded: !!result.expanding },
    }));
    if (!result.expanding) return;
    item.dispatchEvent(new CustomEvent('data-grid-row-details-expand', { bubbles: true, detail: { rowId } }));
}

export function ensureMobileExpandSupport(root, collapseFn, options) {
    if (!(root instanceof HTMLElement)) {
        throw new Error('ensureMobileExpandSupport: root HTMLElement is required');
    }
    const toggleOpenItemById = createToggleRowHelpers(collapseFn || collapseOpenItems);
    bindMobileExpandDelegates(root, toggleOpenItemById, options || {});
    return toggleOpenItemById;
}
